import React, { useEffect, useMemo, useState } from "react";
import SearchComponent from "../../components/search";
import Table from '../../components/datatable/datatable';
import { Link, useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import Dropdown from "../../components/dropdown";
import { adminDeleteApplication, adminFetchAllApplications, deleteApplication } from "../../core/axios/endpoints";
import Papa from "papaparse";
import Swal from "sweetalert2";
import { RoutesFullPath } from "../../utils/constants";
import PartnerInvoiceBuilderModal from "../../components/partner-invoice-builder";
import { PartnerBadge, partnerInvoiceCandidateFromRows } from "../../core/partner-subsidie";


// Lozenge-kleuren per subsidie-status (alleen presentatie; de waarde zelf
// blijft de ruwe status).
const STATUS_LOZENGE = {
    "In-behandeling": "bg-brand-subtle text-brand",
    "Ingediend-bij-rvo": "bg-[#FFF7D6] text-[#7F5F01]",
    "Goedgekeurd": "bg-[#DCFFF1] text-[#1F845A]",
    "Afgekeurd": "bg-[#FFECEB] text-[#AE2A19]",
};

const AdminEIAAanvragenPage = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [SelectedRows, setSeletedRows] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [statistics, setStatistics] = useState({
        totalApplications: 0,
        totalAmount: 0,
    });

    const navigate = useNavigate();

    const handleViewClick = (record) => {
        navigate(`${RoutesFullPath.aanvraagDetailsEIAAdmin}/${record?._id}`, {
            state: { admin: true }
        });
    };

    const [activeSegment, setActiveSegment] = useState('new');
    // Partner-filters (subsidie via partner-link, zie AGENTS.md 3.28).
    const [partnerOnly, setPartnerOnly] = useState(false);
    const [toInvoiceOnly, setToInvoiceOnly] = useState(false);
    const [invoiceBuilderOpen, setInvoiceBuilderOpen] = useState(false);
    const [actionBanner, setActionBanner] = useState(null);

    useEffect(() => {
        fetchData({ page: 1, pageSize: 10, resetPageSize: false });
    }, [activeSegment, partnerOnly, toInvoiceOnly]);

    const closeDropdown = (record) => {
        document.getElementById(`dropdown-klanten-action-${record?.id}`)?.removeAttribute("open");
    };

    const displayName = (row) => row?.klantId?.naam || row?.handelsNaam || "";

    const columns = [
        {
            title: "Naam Klant",
            // Partner-aanvragen hebben geen klantaccount: dan tonen we de
            // handelsnaam met een partner-badge. Sorter is null-safe.
            render: (record) => (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span>{record?.klantId?.naam || record?.handelsNaam || ""}</span>
                    <PartnerBadge row={record} />
                </span>
            ),
            sorter: (a, b) => displayName(a).localeCompare(displayName(b)),
        },
        {
            title: "KVK-Nummer",
            dataIndex: "KvkNummer",
            sorter: (a, b) => a.KvkNummer.localeCompare(b.KvkNummer),
        },
        {
            title: "Kenmerk",
            dataIndex: "Kenmerk",
            sorter: (a, b) => a.Kenmerk.localeCompare(b.Kenmerk),
        },
        {
            title: "Regeling",
            dataIndex: "regling",
            sorter: (a, b) => a.regling.localeCompare(b.regling),
        },
        {
            title: "Datum",
            dataIndex: "datumOpdrachtBevestiging",
            render: (text) => <span>{text}</span>,
            sorter: (a, b) => a.datumOpdrachtBevestiging - b.datumOpdrachtBevestiging,
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${STATUS_LOZENGE[text] ?? "bg-[#091E420F] text-ink-subtle"}`}>
                    {text}
                </span>
            ),
            sorter: (a, b) => a.status.localeCompare(b.status),
        },
        {
            title: "Action",
            render: (record) => (
                <Dropdown className="dropdown-action dropdown dropdown-end" id={"dropdown-klanten-action-" + record?.id}>
                    <summary className="inline-flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] focus:outline-none">
                        <FeatherIcon icon="more-vertical" size={16} />
                    </summary>
                    <ul className="dropdown-content z-[1] flex w-fit min-w-[8rem] flex-col rounded bg-white p-1 shadow-overlay">
                        <a onClick={() => { closeDropdown(record); handleViewClick(record) }}
                            className="link-ancher cursor-pointer rounded px-2 py-1.5 text-sm text-ink hover:bg-[#091E420F]">
                            View
                        </a>
                        {/* <a onClick={() => { closeDropdown(record) }} className="link-ancher cursor-pointer rounded px-2 py-1.5 text-sm text-ink hover:bg-[#091E420F]">
                            Bewerk
                        </a> */}
                    </ul>
                </Dropdown>
            ),
        },
    ];

    const fetchData = async ({ page, pageSize, resetPageSize }) => {
        try {
            setLoading(true);
            const response = await adminFetchAllApplications(page, pageSize, searchText, "EIA", activeSegment, { partner: partnerOnly, toInvoice: toInvoiceOnly });
            setStatistics({
                totalApplications: response.totalCount,
                totalAmount: response.totalAmount,
            });
            const result = {
                // Rijsleutel = _id: de selectie (bulk verwijderen, partner-factuur)
                // werkt op ids, niet op de positie in de pagina.
                data: response.docs.map((application) => ({ ...application, key: application._id })),
                totalDataCount: response.totalDocs,
                resetPageSize,
            };
            setData(result);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ page: 1, pageSize: 10, resetPageSize: false });
    }, [searchText]);

    const handleSearch = (query) => {
        setSearchText(query);
    };

    const handleDelete = async () => {
        Swal.fire({
            title: 'Weet je het zeker?',
            text: "Dit kan niet ongedaan worden gemaakt!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Ja, verwijder het!',
            cancelButtonText: 'Annuleren'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {

                    await adminDeleteApplication(SelectedRows);
                    fetchData({ page: 1, pageSize: 10, resetPageSize: false });
                    setSeletedRows([]);
                    Swal.fire(
                        'Verwijderd!',
                        'De geselecteerde aanvragen zijn verwijderd.',
                        'success'
                    );
                } catch (error) {
                    console.error("Error deleting applications:", error);
                    Swal.fire(
                        'Mislukt!',
                        'Er is een fout opgetreden bij het verwijderen van de aanvragen.',
                        'error'
                    );
                }
            }
        });
    };

    const handleExport = () => {
        const csvData = data.data.map(row => ({
            ID: row._id,
            Naam: row.klantId?.naam || row.handelsNaam || '',
            Partner: row.partnerUserId?.bedrijfsnaam || row.partnerUserId?.naam || '',
            PartnerFactuurStatus: row.partnerInvoice?.status || '',
            KvkNummer: row.KvkNummer,
            VoorLetters: row.VoorLetters,
            Geslacht: row.geslacht,
            PostCode: row.postCode,
            StraatNaam: row.straatNaam,
            Email: row.email,
            Kenmerk: row.Kenmerk,
            MeldCodes: row?.meldCodes,
            HandelsNaam: row.handelsNaam,
            AchterNaam: row.achterNaam,
            HuisNummer: row.huisNummer,
            Plaats: row.plaats,
            TelefoonNummer: row.teleFoonNummer,
            DatumOpdrachtBevestiging: row.datumOpdrachtBevestiging,
            // TotaalVermogen: row.totaalVermogen, // this only available in eia-zon-overzicht
            // AantalZonnepanelen: row.aantalZonnepanelen, // this only available in eia-zon-overzicht
            // Type: row.type, // this only available in isde-overzicht
            Regeling: row.regling,
            Status: row.status,
            ExclusiveBTW: row.exclusiveBTW,
            InclusiveBTW: row.inclusiveBTW,
            Totaal: row.totaal
        }));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "aanvragen.csv");
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const RowChanges = (rows) => {
        setSeletedRows(rows)
        //  
        

    }

    // Knop "Maak partner-factuur": alleen als alle geselecteerde rijen
    // dezelfde partner delen en er minstens één goedgekeurde, nog niet
    // gefactureerde aanvraag tussen zit.
    const partnerInvoiceCandidate = useMemo(() => {
        if (!SelectedRows?.length) return null;
        const ids = new Set(SelectedRows.map(String));
        const rows = (data?.data ?? []).filter((r) => ids.has(String(r._id)));
        return partnerInvoiceCandidateFromRows(rows);
    }, [data, SelectedRows]);

    const formatNumber = (number) => {
        return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      
    return (
        <div className="page-wrapper">
            <div className="content admin-pages">
                <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
                            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
                            <span className="mx-1">/</span>
                            <span>Subsidie-aanvragen</span>
                            <span className="mx-1">/</span>
                            <span>EIA</span>
                        </nav>
                        <h1 className="text-2xl font-medium text-ink">EIA Aanvragen</h1>
                    </div>
                </div>

                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded bg-white p-4 shadow-card">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">EIA Aanvragen</p>
                        <p className="mt-2 text-2xl font-medium tabular-nums text-ink">{statistics?.totalApplications}</p>
                        <p className="mt-1 text-xs text-ink-subtlest">{activeSegment === 'new' ? 'Actief' : 'Oud'}</p>
                    </div>
                    <div className="rounded bg-white p-4 shadow-card">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Totaal Omzet</p>
                        <p className="mt-2 text-2xl font-medium tabular-nums text-ink">€ {formatNumber(statistics?.totalAmount || '0')}</p>
                        <p className="mt-1 text-xs text-ink-subtlest">Over de huidige selectie</p>
                    </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="w-full max-w-md">
                        <SearchComponent onSearch={handleSearch} type={'applications'} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <label className={`inline-flex cursor-pointer items-center gap-2 rounded px-2.5 py-1 text-xs font-medium ${partnerOnly ? 'bg-brand-subtle text-brand' : 'text-ink-subtle hover:bg-[#091E420F]'}`}>
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                                checked={partnerOnly}
                                onChange={(e) => setPartnerOnly(e.target.checked)}
                            />
                            Alleen partner-aanvragen
                        </label>
                        <label className={`inline-flex cursor-pointer items-center gap-2 rounded px-2.5 py-1 text-xs font-medium ${toInvoiceOnly ? 'bg-brand-subtle text-brand' : 'text-ink-subtle hover:bg-[#091E420F]'}`}>
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                                checked={toInvoiceOnly}
                                onChange={(e) => {
                                    setToInvoiceOnly(e.target.checked);
                                    // Te factureren = Goedgekeurd, dat valt onder OUD.
                                    if (e.target.checked) setActiveSegment('old');
                                }}
                            />
                            Te factureren
                        </label>
                        <div className="inline-flex rounded bg-[#091E420F] p-0.5">
                            <button
                                type="button"
                                className={`rounded px-3 py-1 text-sm font-medium transition ${activeSegment === 'new' ? 'bg-white text-brand shadow-card' : 'text-ink-subtle hover:bg-[#091E420F]'}`}
                                onClick={() => setActiveSegment('new')}
                            >
                                Actief
                            </button>
                            <button
                                type="button"
                                className={`rounded px-3 py-1 text-sm font-medium transition ${activeSegment === 'old' ? 'bg-white text-brand shadow-card' : 'text-ink-subtle hover:bg-[#091E420F]'}`}
                                onClick={() => setActiveSegment('old')}
                            >
                                OUD
                            </button>
                        </div>
                    </div>
                </div>

                {SelectedRows?.length ? (
                    <div className="mb-4 flex flex-wrap items-center gap-2 rounded bg-brand-subtle px-3 py-2">
                        <span className="text-sm font-medium text-ink">{SelectedRows.length} geselecteerd</span>
                        <span className="mx-1 text-ink-disabled">·</span>
                        <button
                            type="button"
                            disabled={!SelectedRows?.length}
                            className="inline-flex h-8 items-center gap-1.5 rounded bg-[#C9372C] px-3 text-sm font-medium text-white hover:bg-[#AE2A19] disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleDelete}
                        >
                            <FeatherIcon icon="trash-2" size={16} />
                            Verwijder
                        </button>
                        <button
                            type="button"
                            disabled={!SelectedRows?.length}
                            className="inline-flex h-8 items-center gap-1.5 rounded bg-white px-3 text-sm font-medium text-ink shadow-card hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleExport}
                        >
                            <FeatherIcon icon="download" size={16} />
                            Exporteer
                        </button>
                        {partnerInvoiceCandidate ? (
                            <button
                                type="button"
                                className="inline-flex h-8 items-center gap-1.5 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover"
                                onClick={() => setInvoiceBuilderOpen(true)}
                                title={partnerInvoiceCandidate.blockedCount
                                    ? `${partnerInvoiceCandidate.blockedCount} geselecteerde aanvraag/aanvragen worden overgeslagen (niet goedgekeurd of al gefactureerd).`
                                    : `Factuur voor ${partnerInvoiceCandidate.partnerNaam}`}
                            >
                                <FeatherIcon icon="file-text" size={16} />
                                Maak partner-factuur ({partnerInvoiceCandidate.eligibleIds.length})
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {actionBanner ? (
                    <div className="mb-4 flex items-start justify-between gap-3 rounded bg-[#DCFFF1] px-3 py-2 text-sm text-[#1F845A]">
                        <span className="inline-flex items-start gap-2">
                            <FeatherIcon icon="check-circle" size={16} className="mt-0.5 flex-shrink-0" />
                            <span>
                                {actionBanner.kind === "invoice_sent"
                                    ? `Partner-factuur ${actionBanner.invoiceNumber ?? ""} aangemaakt en verstuurd (${actionBanner.count} regel(s)).`
                                    : `Partner-factuur ${actionBanner.invoiceNumber ?? ""} aangemaakt als concept (${actionBanner.count} regel(s)).`}
                            </span>
                        </span>
                        <button type="button" className="text-xs font-medium underline" onClick={() => setActionBanner(null)}>Sluiten</button>
                    </div>
                ) : null}

                <div className="table-responsive rounded bg-white shadow-card">
                    <Table
                        columns={columns}
                        onRowClick={handleViewClick}
                        response={data}
                        fetchData={fetchData}
                        SelectedRows={RowChanges}
                    />
                </div>
            </div>

            {invoiceBuilderOpen && partnerInvoiceCandidate ? (
                <PartnerInvoiceBuilderModal
                    isOpen={invoiceBuilderOpen}
                    onClose={() => setInvoiceBuilderOpen(false)}
                    applicationModel="Applications"
                    partnerUserId={partnerInvoiceCandidate.partnerUserId}
                    applicationIds={partnerInvoiceCandidate.eligibleIds}
                    onCreated={(invoice, { sent }) => {
                        setInvoiceBuilderOpen(false);
                        setSeletedRows([]);
                        setActionBanner({
                            kind: sent ? "invoice_sent" : "invoice_draft",
                            count: invoice?.lines?.length ?? 0,
                            invoiceNumber: invoice?.invoiceNumber,
                        });
                        fetchData({ page: 1, pageSize: 10, resetPageSize: false });
                    }}
                />
            ) : null}
        </div>
    );
};

export default AdminEIAAanvragenPage;
